import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Heading, Paragraph, Text, Anchor } from '@zillow/constellation';
import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { getCommaSeparatedNumber, commaSeparateNumber } from '../../utils/zhl-calculator.js';
import TextField from '../Fields/TextField.js';
import { triggerFormFieldEngagementClickstreamEvent } from './HomeSaleProceedsCalculater.clickstream.js';
import { FieldConfig, validateAndTrackOptionsBlock } from './HomeSaleProceedsCalculator.utils.js';

var TOOLTIPS = {
  homeImprovement: {
    title: 'Home improvement',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillow.com/research/sellers-housing-trends-report-2024-34385/",
      target: "_blank"
    }, "Seventy-two percent of sellers"), ' ', "make at least one improvement. Common improvements include: kitchen updates, bathroom improvement, replacing or purchasing new appliances, landscaping, replacing or repairing flooring, and roof repairs.")
  },
  cleaningAndPrep: {
    title: 'Cleaning and prep',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "Professional home preparation, including: home staging, cleaning, interior and exterior painting, full-service lawn care, and carpet cleaning.")
  },
  monthlyMortgagePayments: {
    title: 'Monthly mortgage payments',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "While a home is on the market, sellers continue paying their monthly mortgage payment up until closing.")
  },
  moving: {
    title: 'Moving',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "Relocating to temporary housing or to a new home will cost moving and often storage expenses out-of-pocket, prior to receiving your sale proceeds.")
  },
  agentCommission: {
    title: 'Agent commission',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "Sellers typically pay both their listing agent and the buyer agent's commissions, which come out to be an average of 2-3% per agent. Agent fees are negotiable at the onset of your partnership.", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillow.com/research/cost-selling-house-tax-commission-23717/"
    }, "Learn more"))
  },
  sellingConcessions: {
    title: 'Selling concessions',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillow.com/learn/seller-concessions/"
    }, "Seller concessions"), ' ', "are items the seller agrees to pay for on behalf of the buyer at closing. Common examples include a seller agreeing to cover part of a buyer's closing costs, or offering to cover the cost of needed repairs uncovered during the home inspection. While seller concessions are common, they're not included in every offer.")
  },
  closingFees: {
    title: 'Closing fees',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "Home sale transaction costs include title insurance, escrow fees, possible attorney fees, and HOA fees which will be prorated based on your closing date.")
  },
  taxes: {
    title: 'Taxes',
    body: /*#__PURE__*/React.createElement(Paragraph, {
      marginRight: "sm"
    }, "Sellers pay local transfer taxes that vary based on city and state. Property taxes will be prorated with your closing date and you may owe capital gains tax after the transaction.")
  }
};
var HomeSaleProceedsFields = function HomeSaleProceedsFields() {
  var _useFormContext = useFormContext(),
    setValue = _useFormContext.setValue,
    getValues = _useFormContext.getValues,
    watch = _useFormContext.watch;
  var _useState = useState({
      homeImprovement: 0,
      cleaningAndPrep: 1.9867,
      monthlyMortgagePayments: 0,
      moving: 0.1657,
      agentCommission: 6,
      sellingConcessions: 1,
      closingFees: 0.5,
      taxes: 1
    }),
    _useState2 = _slicedToArray(_useState, 2),
    rightAdornments = _useState2[0],
    setRightAdornments = _useState2[1];

  // Watch for desiredSellingPrice field change without triggering a re-render
  // const desiredSellingPrice = watch(FieldConfig.desiredSellingPrice.name);

  useEffect(function () {
    var _watch = watch(function (data, info) {
        if (info.name !== FieldConfig.desiredSellingPrice.name) {
          return;
        }
        var newValues = {};
        var desiredSellingPrice = data.desiredSellingPrice || 0;
        Object.entries(rightAdornments).forEach(function (_ref) {
          var _FieldConfig$key;
          var _ref2 = _slicedToArray(_ref, 2),
            key = _ref2[0],
            percentage = _ref2[1];
          var fieldName = (_FieldConfig$key = FieldConfig[key]) === null || _FieldConfig$key === void 0 ? void 0 : _FieldConfig$key.name;
          if (fieldName) {
            newValues[fieldName] = Math.round(desiredSellingPrice * percentage / 100);
          }
        });
        // Update all dependent fields in one go
        Object.entries(newValues).forEach(function (_ref3) {
          var _ref4 = _slicedToArray(_ref3, 2),
            field = _ref4[0],
            value = _ref4[1];
          setValue(field, getCommaSeparatedNumber(value), validateAndTrackOptionsBlock);
        });
      }),
      unsubscribe = _watch.unsubscribe;
    return function () {
      return unsubscribe();
    };
  }, [setValue]);
  var handleManualFieldChange = function handleManualFieldChange(name) {
    return function (event) {
      var inputValue = event.target.value.trim().replace(/,/g, '');
      var newValue = inputValue === '' ? 0 : Number(inputValue);
      setValue(name, getCommaSeparatedNumber(newValue), validateAndTrackOptionsBlock);
      // Update percentage dynamically
      var result = newValue / getValues().desiredSellingPrice * 100;
      if (!isFinite(result) || isNaN(result)) {
        result = 0;
      }
      setRightAdornments(function (prev) {
        var _Object$assign;
        return Object.assign({}, prev, (_Object$assign = {}, _Object$assign[name] = parseFloat(result.toFixed(2)), _Object$assign));
      });
    };
  };
  var handleEmptyDesireSellingPriceField = function handleEmptyDesireSellingPriceField(name) {
    return function (event) {
      var inputValue = event.target.value.trim().replace(/,/g, '');
      if (inputValue === '') {
        setValue(name, 0, validateAndTrackOptionsBlock);
      }
    };
  };
  var setRightAdornmentValue = function setRightAdornmentValue(number) {
    return Number.isInteger(number) ? number + ' %' : number.toFixed(2) + ' %';
  };
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Selling price & mortgage"), /*#__PURE__*/React.createElement(Paragraph, {
    paddingBottom: "sm"
  }, "The amount you'd like to sell your home for and total remaining mortgage amount."), /*#__PURE__*/React.createElement(TextField, {
    label: "Desired selling price",
    leftAdornment: "$",
    name: FieldConfig.desiredSellingPrice.name,
    config: FieldConfig.desiredSellingPrice.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Desired selling price');
    },
    onChange: handleEmptyDesireSellingPriceField(FieldConfig.desiredSellingPrice.name),
    required: true
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Remaining mortgage owed",
    leftAdornment: "$",
    name: FieldConfig.remainingMortgageOwed.name,
    config: FieldConfig.remainingMortgageOwed.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Remaining mortgage owed');
    },
    onChange: handleManualFieldChange(FieldConfig.remainingMortgageOwed.name)
  }), /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "xs",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Estimated prep & repair costs"), /*#__PURE__*/React.createElement(Text, {
    fontType: "bodySmall",
    as: "p",
    marginBottom: "xs"
  }, "Average 35 days"), /*#__PURE__*/React.createElement(Paragraph, {
    paddingBottom: "sm"
  }, "All the work you need to complete before you're ready to list."), /*#__PURE__*/React.createElement(TextField, {
    label: "Home improvement",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.homeImprovement),
    name: FieldConfig.homeImprovement.name,
    config: FieldConfig.homeImprovement.config,
    onChange: handleManualFieldChange(FieldConfig.homeImprovement.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Home improvement');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.homeImprovement
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Cleaning and prep",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.cleaningAndPrep),
    name: FieldConfig.cleaningAndPrep.name,
    config: FieldConfig.cleaningAndPrep.config,
    onChange: handleManualFieldChange(FieldConfig.cleaningAndPrep.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Cleaning and prep');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.cleaningAndPrep
  }), /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "xs",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Estimated on-market costs"), /*#__PURE__*/React.createElement(Text, {
    fontType: "bodySmall",
    as: "p",
    marginBottom: "xs"
  }, "Average 68 days"), /*#__PURE__*/React.createElement(Paragraph, {
    paddingBottom: "sm"
  }, "The out-of-pocket costs of transitioning your life from one home to another."), /*#__PURE__*/React.createElement(TextField, {
    label: "Monthly mortgage payments",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.monthlyMortgagePayments),
    name: FieldConfig.monthlyMortgagePayments.name,
    config: FieldConfig.monthlyMortgagePayments.config,
    onChange: handleManualFieldChange(FieldConfig.monthlyMortgagePayments.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Monthly mortgage payments');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.monthlyMortgagePayments
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Moving",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.moving),
    name: FieldConfig.moving.name,
    config: FieldConfig.moving.config,
    onChange: handleManualFieldChange(FieldConfig.moving.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Moving');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.moving
  }), /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "xs",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Estimated closing costs"), /*#__PURE__*/React.createElement(Text, {
    fontType: "bodySmall",
    as: "p",
    marginBottom: "xs"
  }, "Average 45 days"), /*#__PURE__*/React.createElement(Paragraph, {
    paddingBottom: "sm"
  }, "The standard costs of the home sale transaction, paid at closing."), /*#__PURE__*/React.createElement(TextField, {
    label: "Agent commission",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.agentCommission),
    name: FieldConfig.agentCommission.name,
    config: FieldConfig.agentCommission.config,
    onChange: handleManualFieldChange(FieldConfig.agentCommission.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Agent commission');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.agentCommission
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Selling concessions",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.sellingConcessions),
    name: FieldConfig.sellingConcessions.name,
    config: FieldConfig.sellingConcessions.config,
    onChange: handleManualFieldChange(FieldConfig.sellingConcessions.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Selling concessions');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.sellingConcessions
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Closing fees",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.closingFees),
    name: FieldConfig.closingFees.name,
    config: FieldConfig.closingFees.config,
    onChange: handleManualFieldChange(FieldConfig.closingFees.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Closing fees');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.closingFees
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Taxes",
    leftAdornment: "$",
    rightAdornment: setRightAdornmentValue(rightAdornments.taxes),
    name: FieldConfig.taxes.name,
    config: FieldConfig.taxes.config,
    onChange: handleManualFieldChange(FieldConfig.taxes.name),
    onBlur: function onBlur() {
      return triggerFormFieldEngagementClickstreamEvent('Taxes');
    },
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: TOOLTIPS.taxes
  }));
};
var HomeSaleProceedsFields$1 = HomeSaleProceedsFields;

export { HomeSaleProceedsFields$1 as default };
